import { onMounted, onUnmounted } from 'vue';

import { acertoPwaInstalado, type DeteccaoInstalacaoPwa } from '@/modules/events/acerto';
import {
  CHAVE_INSTALACAO_REGISTRADA,
  calcularJaInstalado,
  gravarMarcaLocal,
  lerMarcaLocal,
} from '@shared/pwa/utils/instalacaoPwa';
import { detectarPlataformaPwa } from '@shared/pwa/utils/plataformaPwa';
import { pedirPermissaoEAssinarPush, precarregarChavePublicaVapid } from '@shared/pwa/utils/pushSubscription';
import { pwaHabilitado } from '@shared/pwa/utils/pwaHabilitado';

/** Evento `beforeinstallprompt`, ainda sem tipagem oficial no DOM lib do TypeScript. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const CHAVE_INSTALACAO_DISPENSADA = 'pwa-instalacao-dispensada';

/**
 * Tags que tornam o portal instalável e dão a ele a cara de app da Acerto. Ficam fora do
 * `index.html` porque, com elas estáticas, o próprio navegador ofereceria instalar o app da
 * Acerto (ícone na barra de endereço, menu "Instalar app") também no tema de parceiro.
 */
const METADADOS_DO_PWA: ReadonlyArray<{ tag: 'link' | 'meta'; atributos: Record<string, string> }> = [
  { tag: 'link', atributos: { rel: 'manifest', href: '/manifest.webmanifest' } },
  { tag: 'meta', atributos: { name: 'theme-color', content: '#FFFFFF' } },
  { tag: 'link', atributos: { rel: 'apple-touch-icon', href: '/icons/apple-touch-icon.png' } },
  { tag: 'meta', atributos: { name: 'apple-mobile-web-app-capable', content: 'yes' } },
  { tag: 'meta', atributos: { name: 'apple-mobile-web-app-status-bar-style', content: 'default' } },
  // Sem ela, o Safari sugere o `<title>` da página como nome do app na tela de início.
  { tag: 'meta', atributos: { name: 'apple-mobile-web-app-title', content: 'Acerto' } },
];

function aplicarMetadadosDoPwa() {
  for (const { tag, atributos } of METADADOS_DO_PWA) {
    const [chave, valor] = Object.entries(atributos)[0];
    if (document.head.querySelector(`${tag}[${chave}="${valor}"]`)) continue;

    const elemento = document.createElement(tag);
    for (const [nome, conteudo] of Object.entries(atributos)) {
      elemento.setAttribute(nome, conteudo);
    }
    document.head.appendChild(elemento);
  }
}

/**
 * Uma vez por dispositivo: no Android o app instalado divide o armazenamento com o navegador, então
 * a marca gravada no `appinstalled` evita contar de novo na primeira abertura do app.
 */
function registrarInstalacao(deteccao: DeteccaoInstalacaoPwa) {
  if (lerMarcaLocal(CHAVE_INSTALACAO_REGISTRADA)) return;

  acertoPwaInstalado({ plataforma: detectarPlataformaPwa(), deteccao });
  gravarMarcaLocal(CHAVE_INSTALACAO_REGISTRADA);
}

/**
 * Captura o `beforeinstallprompt` assim que o app monta (precisa ser cedo: o navegador só
 * entrega o evento a listeners já registrados no momento do disparo) e o mantém disponível
 * em `window.__pwaDeferredPrompt`. Também expõe em `window` os sinais de que o convite de
 * instalação NÃO deve ser exibido (`__pwaJaInstalado`, `__pwaInstalacaoDispensada`) e a
 * função para marcar a dispensa (`__pwaMarcarInstalacaoDispensada`). O convite em si (CTA,
 * modal, instrução do iOS) é responsabilidade de outra ferramenta (Adoptly), que deve checar
 * esses sinais antes de exibir, e chamar `window.__pwaDeferredPrompt.prompt()` quando o
 * usuário aceita.
 */
function atualizarJaInstalado() {
  window.__pwaJaInstalado = calcularJaInstalado();
}

/**
 * A Adoptly chama `.prompt()` direto no evento exposto em `window`, então o pedido de
 * permissão de push é encadeado aqui para disparar logo após a escolha no convite de
 * instalação, seja ela aceitar ou recusar, sem depender de código na Adoptly.
 */
function encadearPushAposEscolhaDeInstalacao(evento: BeforeInstallPromptEvent) {
  const promptNativo = evento.prompt.bind(evento);
  evento.prompt = async () => {
    await promptNativo();
    await evento.userChoice;
    void pedirPermissaoEAssinarPush();
  };
}

function aoCapturarBeforeInstallPrompt(event: Event) {
  event.preventDefault();
  const evento = event as BeforeInstallPromptEvent;
  encadearPushAposEscolhaDeInstalacao(evento);
  window.__pwaDeferredPrompt = evento;
}

function aoCapturarAppInstalado() {
  window.__pwaDeferredPrompt = null;
  window.__pwaJaInstalado = true;
  registrarInstalacao('appinstalled');
}

function marcarInstalacaoDispensada() {
  window.__pwaInstalacaoDispensada = true;
  gravarMarcaLocal(CHAVE_INSTALACAO_DISPENSADA);
}

export function useInstalacaoPwa() {
  let mediaQueryStandalone: MediaQueryList | undefined;

  onMounted(() => {
    window.__pwaHabilitado = pwaHabilitado();
    if (!window.__pwaHabilitado) return;

    window.__pwaInstalacaoDispensada = lerMarcaLocal(CHAVE_INSTALACAO_DISPENSADA);
    window.__pwaMarcarInstalacaoDispensada = marcarInstalacaoDispensada;
    window.__pwaPedirPermissaoPush = pedirPermissaoEAssinarPush;
    // No mount, e não no toque do convite: no iOS o `subscribe` precisa sair logo depois da permissão.
    void precarregarChavePublicaVapid();
    atualizarJaInstalado();
    if (window.__pwaJaInstalado) registrarInstalacao('primeira_abertura');

    if (typeof window.matchMedia === 'function') {
      mediaQueryStandalone = window.matchMedia('(display-mode: standalone)');
      mediaQueryStandalone.addEventListener('change', atualizarJaInstalado);
    }

    window.addEventListener('beforeinstallprompt', aoCapturarBeforeInstallPrompt);
    window.addEventListener('appinstalled', aoCapturarAppInstalado);
    aplicarMetadadosDoPwa();
  });

  onUnmounted(() => {
    window.removeEventListener('beforeinstallprompt', aoCapturarBeforeInstallPrompt);
    window.removeEventListener('appinstalled', aoCapturarAppInstalado);
    mediaQueryStandalone?.removeEventListener('change', atualizarJaInstalado);
  });
}
